import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';

import { DateRange } from 'materialui-daterange-picker';
import { Page } from 'ui/components/Page/Page';
import { TabPanel } from 'ui/components/TabPanel';
import DateRangePicker from 'ui/components/TextField/DateRangePicker/DateRangePicker';
import {
  fetchGeneralWidgetData,
  fetchProfitData,
  fetchPurchasingExpenses,
  fetchSalesByRegions,
  fetchSalesTotalRevenue,
} from 'services/dashboard/api';
import {
  ChartData,
  GeneralWidgetValues,
  RegionalSaleOrder,
} from 'services/dashboard/types';
import {
  initialFromDateRange,
  initialGeneralWidgetValues,
  initialToDateRange,
} from 'services/dashboard/consts';

import {
  DashboardLineChart,
  DashboardWidgets,
  DashboardGeneral,
  DashboardSales,
  DashboardPurchasing,
} from './components';
import { Routes } from '../../navigation';
import { DashboardPageCmp, DashboardPageProps } from './types';
import { useDashboardPageStyle } from './styled';
import {
  DATE_FORMAT,
  lineChartDataGeneral,
  lineChartDataPurchasing,
  lineChartDataSales,
} from './consts';
import { useDispatch } from 'react-redux';
import { fetchSettingsCompanies } from 'services/settings/company';

const DashboardPage: DashboardPageCmp = (props: DashboardPageProps) => {
  const classes = useDashboardPageStyle(props);

  const [activeTab, setActiveTab] = useState(0);
  const [fromDateRange, setFromDateRange] =
    useState<DateRange>(initialFromDateRange);
  const [toDateRange, setToDateRange] = useState<DateRange>(initialToDateRange);
  const [generalWidgetData, setGeneralWidgetData] =
    useState<GeneralWidgetValues>(initialGeneralWidgetValues);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [chartTitle, setChartTitle] = useState('PROFIT');
  const [salesByRegion, setSalesByRegion] = useState<RegionalSaleOrder[]>([]);
  const [nonAddressedOrder, setNonAdressedOrder] =
    useState<RegionalSaleOrder | null>(null);

  const dispatch = useDispatch();

  const fromStartMoment = useMemo(
    () => moment(fromDateRange.startDate),
    [fromDateRange.startDate]
  );
  const fromEndMoment = useMemo(
    () => moment(fromDateRange.endDate),
    [fromDateRange.endDate]
  );
  const fromDiff = useMemo(
    () => Math.abs(fromStartMoment.diff(fromEndMoment, 'days')) + 1,
    [fromStartMoment, fromEndMoment]
  );
  const toStartMoment = useMemo(
    () => moment(toDateRange.startDate),
    [toDateRange.startDate]
  );
  const toEndMoment = useMemo(
    () => moment(toDateRange.endDate),
    [toDateRange.endDate]
  );

  useEffect(() => {
    // Lint skip to be removed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    dispatch(fetchSettingsCompanies());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const activeLineChartData = useMemo(() => {
    switch (activeTab) {
      case 0:
        return lineChartDataGeneral;
      case 1:
        return lineChartDataSales;
      case 2:
        return lineChartDataPurchasing;
    }

    return lineChartDataGeneral;
  }, [activeTab]);

  const getSalesByRegion = useCallback(async () => {
    if (!fromStartMoment.isValid() || !fromEndMoment.isValid()) {
      return;
    }

    try {
      const resSalesByRegion = await fetchSalesByRegions(
        fromStartMoment.format(DATE_FORMAT),
        fromEndMoment.format(DATE_FORMAT)
      );

      setNonAdressedOrder(resSalesByRegion.nonAddressedOrder);
      setSalesByRegion(resSalesByRegion.salesByRegion);
    } catch {
      setSalesByRegion([]);
    }
  }, [fromStartMoment, fromEndMoment]);

  const handleWidgetData = useCallback(async () => {
    if (
      !fromStartMoment.isValid() ||
      !fromEndMoment.isValid() ||
      !toStartMoment.isValid() ||
      !toEndMoment.isValid()
    ) {
      return;
    }

    try {
      const data = await fetchGeneralWidgetData(
        fromStartMoment.format(DATE_FORMAT),
        fromEndMoment.format(DATE_FORMAT),
        toStartMoment.format(DATE_FORMAT),
        toEndMoment.format(DATE_FORMAT)
      );
      setGeneralWidgetData(data);
    } catch {
      setGeneralWidgetData(initialGeneralWidgetValues);
    }
  }, [fromStartMoment, fromEndMoment, toStartMoment, toEndMoment]);

  const handleGeneralTabData = useCallback(async () => {
    setChartTitle('PROFIT');
    const profit = await fetchProfitData(
      fromStartMoment.format(DATE_FORMAT),
      fromEndMoment.format(DATE_FORMAT),
      toStartMoment.format(DATE_FORMAT),
      toEndMoment.format(DATE_FORMAT)
    );
    setChartData(profit);
  }, [fromStartMoment, fromEndMoment, toStartMoment, toEndMoment]);

  const handleSalesTabData = useCallback(async () => {
    setChartTitle('REVENUE');
    const revenue = await fetchSalesTotalRevenue(
      fromStartMoment.format(DATE_FORMAT),
      fromEndMoment.format(DATE_FORMAT),
      toStartMoment.format(DATE_FORMAT),
      toEndMoment.format(DATE_FORMAT)
    );
    setChartData(revenue);
  }, [fromStartMoment, fromEndMoment, toStartMoment, toEndMoment]);

  const handlePurchaseTabData = useCallback(async () => {
    setChartTitle('EXPENSES');
    const expenses = await fetchPurchasingExpenses(
      fromStartMoment.format(DATE_FORMAT),
      fromEndMoment.format(DATE_FORMAT),
      toStartMoment.format(DATE_FORMAT),
      toEndMoment.format(DATE_FORMAT)
    );
    setChartData(expenses);
  }, [fromStartMoment, fromEndMoment, toStartMoment, toEndMoment]);

  const handleTabChange = useCallback(
    async (event: React.ChangeEvent<{}>, newValue: number) => {
      setActiveTab(newValue);
    },
    []
  );

  useEffect(() => {
    (async () => {
      getSalesByRegion();
      handleWidgetData();
      switch (activeTab) {
        case 0:
          handleGeneralTabData();
          break;
        case 1:
          handleSalesTabData();
          break;
        case 2:
          handlePurchaseTabData();
          break;
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDateRange, toDateRange, activeTab]);

  const handleFromDateChange = useCallback((value: DateRange) => {
    const fromMoment = moment(value.startDate);
    const toMoment = moment(value.endDate);
    const diff = Math.abs(fromMoment.diff(toMoment, 'days'));

    setFromDateRange(value);
    setToDateRange({
      startDate: fromMoment
        .clone()
        .add(-1 * diff - 1, 'days')
        .toDate(),
      endDate: fromMoment.clone().add(-1, 'day').toDate(),
    });
  }, []);
  const handleToDateChange = useCallback(
    (value: DateRange) => {
      setToDateRange(value);
    },
    [setToDateRange]
  );

  return (
    <Page>
      <Box className={classes.container}>
        <Box className={classes.sidebar}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            className={classes.tabs}
            orientation="vertical"
            indicatorColor="secondary"
          >
            <Tab
              label="General"
              icon={<AssessmentIcon />}
              className={classes.tab}
              data-qa="general-tab"
            />
            <Tab
              label="Sales"
              icon={<MonetizationOnOutlinedIcon />}
              className={classes.tab}
              data-qa="sales-tab"
            />
            <Tab
              label="Purchasing"
              icon={<WidgetsOutlinedIcon />}
              className={classes.tab}
              data-qa="purchasing-tab"
            />
          </Tabs>

          <Box borderTop="1px solid #EBEBEB" mt={2} pt={3}>
            <Box mb={2}>
              <Box mb={1} pl={1.85}>
                <Typography variant="body2" color="textSecondary">
                  From
                </Typography>
              </Box>
              <DateRangePicker
                value={fromDateRange}
                label="Month to Date"
                name="dateRange"
                placeholder="Select Date Range"
                onChange={handleFromDateChange}
                fullWidth
                dataQa="home-from-date"
              />
            </Box>
            <Box mb={1} pl={1.85}>
              <Typography variant="body2" color="textSecondary">
                Compared to
              </Typography>
            </Box>
            <DateRangePicker
              value={toDateRange}
              label="Previous Month"
              name="dateRange2"
              placeholder="Select Date Range"
              onChange={handleToDateChange}
              rangeDays={fromDiff}
              fullWidth
              dataQa="previous-month-box"
            />
          </Box>
        </Box>

        <Box className={classes.content}>
          <DashboardWidgets data={generalWidgetData} />

          <DashboardLineChart
            title={chartTitle}
            data={chartData}
            lineChartData={activeLineChartData}
          />

          <TabPanel value={activeTab} index={0} noSpacing overflow>
            <DashboardGeneral
              fromStartDate={fromStartMoment}
              fromEndDate={fromEndMoment}
              salesByRegion={salesByRegion}
              setSalesByRegion={setSalesByRegion}
              nonAddressedOrder={nonAddressedOrder}
            />
          </TabPanel>
          <TabPanel value={activeTab} index={1} noSpacing overflow>
            <DashboardSales
              fromStartDate={fromStartMoment}
              fromEndDate={fromEndMoment}
            />
          </TabPanel>
          <TabPanel value={activeTab} index={2} noSpacing overflow>
            <DashboardPurchasing
              fromStartDate={fromStartMoment}
              fromEndDate={fromEndMoment}
              toStartMoment={toStartMoment}
              toEndMoment={toEndMoment}
            />
          </TabPanel>
        </Box>
      </Box>
    </Page>
  );
};

DashboardPage.route = Routes.DashboardPage;

export default DashboardPage;
